import React, { useState, useEffect } from 'react';
import {
  People,
  LocalHospital,
  Assignment,
  BarChart,
  ReceiptLong,
  Category as CategoryIcon,
  Science as SampleTypeIcon,
  LocationOn as LocationIcon,
  Storage as CmsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import SwalWithA11yFix from '../../services/swalConfig';
import UserManagement from '../../components/admin/UserManagement';
import LabManagement from '../../components/admin/LabManagement';
import TestManagement from '../../components/admin/TestManagement';
import OrderManagement from '../../components/admin/OrderManagement';
import Statistics from '../../components/admin/Statistics';
import CategoryManagement from '../../components/admin/CategoryManagement';
import SampleTypeManagement from '../../components/admin/SampleTypeManagement';
import LocationManagement from '../../components/admin/LocationManagement';
import DirectReportSharing from '../../components/admin/DirectReportSharing';
import DashboardLayout from '../../components/common/DashboardLayout';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, labsRes, testsRes] = await Promise.allSettled([
        api.get('/admin/dashboard/stats'),
        api.get('/admin/labs'),
        api.get('/admin/tests'),
      ]);

      const dashboardStats = statsRes.status === 'fulfilled' ? statsRes.value.data : {};
      const labs = labsRes.status === 'fulfilled' && Array.isArray(labsRes.value.data) ? labsRes.value.data : [];
      const tests = testsRes.status === 'fulfilled' && Array.isArray(testsRes.value.data) ? testsRes.value.data : [];

      setStats({
        ...dashboardStats,
        // Use the same data sources as Lab Management and Test Management for exact parity.
        labCount: labs.length,
        testCount: tests.length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const result = await SwalWithA11yFix({
      title: 'Are you sure?',
      text: 'You will be logged out of your account',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1976d2',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, logout',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      logout();
      await SwalWithA11yFix({
        icon: 'success',
        title: 'Logged Out!',
        text: 'You have been successfully logged out.',
        timer: 1500,
        showConfirmButton: false,
      });
      navigate('/admin-login');
    }
  };

  const menuItems = [
    {
      icon: <BarChart />,
      label: 'Statistics',
      active: activeTab === 0,
      onClick: () => setActiveTab(0),
    },
    {
      icon: <People />,
      label: 'User Management',
      active: activeTab === 1,
      onClick: () => setActiveTab(1),
    },
    {
      icon: <LocalHospital />,
      label: 'Lab Management',
      active: activeTab === 2,
      onClick: () => setActiveTab(2),
    },
    {
      icon: <Assignment />,
      label: 'Test Management',
      active: activeTab === 3,
      onClick: () => setActiveTab(3),
    },
    {
      icon: <ReceiptLong />,
      label: 'Orders',
      active: activeTab === 4,
      onClick: () => setActiveTab(4),
    },
    {
      icon: <ReceiptLong />,
      label: 'Direct Reports',
      active: activeTab === 5,
      onClick: () => setActiveTab(5),
    },
    {
      icon: <CategoryIcon />,
      label: 'Categories',
      active: activeTab === 6,
      onClick: () => setActiveTab(6),
    },
    {
      icon: <SampleTypeIcon />,
      label: 'Sample Types',
      active: activeTab === 7,
      onClick: () => setActiveTab(7),
    },
    {
      icon: <LocationIcon />,
      label: 'Locations',
      active: activeTab === 8,
      onClick: () => setActiveTab(8),
    },
    {
      icon: <CmsIcon />,
      label: 'CMS',
      href: process.env.REACT_APP_CMS_URL || 'http://guide.analytikaldna.com/login',
      external: true,
      isExternal: true,
    },
  ];

  const getTabHeading = () => {
    const headings = [
      'Statistics',
      'User Management',
      'Lab Management',
      'Test Management',
      'Orders',
      'Direct Reports',
      'Categories',
      'Sample Types',
      'Locations',
    ];
    return headings[activeTab] || 'Admin Dashboard';
  };

  return (
    <DashboardLayout
      title="Admin Dashboard"
      user={user}
      onLogout={handleLogout}
      menuItems={menuItems}
      logoutPath="/admin-login"
      topHeading={getTabHeading()}
    >
      {activeTab === 0 && <Statistics stats={stats} loading={loading} />}
      {activeTab === 1 && <UserManagement onUpdate={fetchStats} />}
      {activeTab === 2 && <LabManagement />}
      {activeTab === 3 && <TestManagement />}
      {activeTab === 4 && <OrderManagement />}
      {activeTab === 5 && <DirectReportSharing />}
      {activeTab === 6 && <CategoryManagement />}
      {activeTab === 7 && <SampleTypeManagement />}
      {activeTab === 8 && <LocationManagement />}
    </DashboardLayout>
  );
};

export default AdminDashboard;
